/**
 * Copyright 2021 Olympe S.A.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
// @ts-nocheck
import { registerBrick, BrickContext, Auth,  Brick, User, Query} from 'olympe';
import { ReactBrick, useProperty,
    ifNotTransparent,
    ifNotNull } from '@olympeio/core';
import { useOlympeTheme } from './mui';
import { ThemeProvider } from '@mui/material/styles';
import React, {useEffect, useState} from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Icon from '@mui/material/Icon';
import TextField from '@mui/material/TextField';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import { loadIconsSet } from "./icons";

/**
 * Provide a Button visual component using MUI Button
 */
export default class Header extends ReactBrick {

    /**
     * @override
     */
    init() {
        loadIconsSet('outlined');
    }

    /**
     * @override
     */
    setupExecution($: BrickContext) {
        return $.observe('Hidden');
    }

    /**
     * @override
     */
    updateParent(parent: HTMLElement, element: HTMLElement) {
        parent.style.overflow = 'visible'; // Allow overflow for menu
        return super.updateParent(parent, element);
    }

    /**
     * @override
     */
    static getReactComponent($: BrickContext) {
        return (props: { values: [hidden: boolean] }) => {
            const [hidden] = props.values;
            const theme = useOlympeTheme($);
            const [isMenuOpen, setMenuOpen] = useState(false);
            const [isSearchOpen, setSearchOpen] = useState(false);
            const [latestSearchTerm, setLatestSearchTerm] = useState<string>();
            const [userSlang, setUserSlang] = useState<string>('-');
            const inputRef = React.useRef(null);

            const title = useProperty($, 'Title', true);
            const subtitle = useProperty($, 'Subtitle', true);
            const notifCount = useProperty($, 'Notif Count', true);
            const searchedText = useProperty($, 'Searched Text', true);
            const borderColor = useProperty($, 'Border Color', true);
            const borderRadius = useProperty($, 'Border Radius', true);
            const borderWidth = useProperty($, 'Border Width', true);
            const defaultColor = useProperty($, 'Default Color', true);
            const hideSearch = useProperty($, 'Hide Search', true);
            const hideNotifications = useProperty($, 'Hide Notifications', true);
            const hiderUserIcon = useProperty($, 'Hide User Icon', true);
            const userMenuList = useProperty($, 'User Menu List', true);

            const customSx = {
                ...ifNotTransparent('borderColor', borderColor),
                ...ifNotNull('borderRadius', `${borderRadius}px`, borderRadius),
                ...ifNotNull('borderWidth', borderWidth),
                ...ifNotTransparent('borderStyle', 'solid', borderColor),
                ...ifNotTransparent('backgroundColor', defaultColor),
            };

            const iconStyle = {
                backgroundColor: theme.palette.secondary.main,
                color: theme.palette.secondary.dark,
                borderRadius: 20,
                width: '40px',
                height: '40px',
                marginRight: '15px',
                lineHeight: '40px !important',
                cursor: 'pointer',
                position: 'relative',
                overflow: 'visible',
                zIndex: 2
            };

            const openSearch = () => {
                if (!isSearchOpen) {
                    setSearchOpen(true);
                } else {
                    performSearch();
                }
                inputRef.current.focus();
            };

            const performSearch = () => {
                const currentSearchTerm  = inputRef.current?.value;
                if (currentSearchTerm !== latestSearchTerm) {
                    setLatestSearchTerm(currentSearchTerm);
                    $.trigger('On Search');
                }
            }

            const handleSearchEnd = () => {
                const currentSearchTerm  = inputRef.current?.value;
                if (currentSearchTerm === '') {
                    setSearchOpen(false);
                }
            }

            const handleUserMenuClick = (menuName) => {
                const onUserMenuClickProp = $.get<Brick>("On User Menu Click");
                if (onUserMenuClickProp) {
                    const onUserMenuClick$ = $.runner(onUserMenuClickProp)
                    onUserMenuClick$.set(onUserMenuClickProp.getInputs()[1], menuName);
                    onUserMenuClick$.trigger(onUserMenuClickProp.getInputs()[0]);
                    // TODO: should we destroy ?
                }
            }

            useEffect(async () => {
                const result = await Query.fromTag(Auth.getCurrentUser(), User).execute();
                const login = result.getFirst().get(User.loginProp);
                let slang = login?.substring(0, 2);
                if (login === 'guest' || login === null) {
                    slang = '-';
                } else if (login.split('.').length > 1) {
                    slang = login.split('.')[0].substring(0, 1) + login.split('.')[1].substring(0, 1);
                } else if (login.split(' ').length > 1) {
                    slang = login.split(' ')[0].substring(0, 1) + login.split(' ')[1].substring(0, 1);
                }
                setUserSlang(slang.toUpperCase());
            }, [Auth.observeUser($)])

            const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);


            return (
                !hidden && (
                    <ThemeProvider theme={theme}>
                        <Box sx={{width: 1, height: 1, display: 'flex', alignItems: 'center', justifyContent: 'space-between', ...customSx}}>
                            <Box  sx={{display: 'flex', flexDirection: 'column', justifyContent: 'center'}}>
                                <Typography component={'h1'} noWrap={true}
                                    sx={{
                                        fontSize: '25px',
                                        fontWeight: 'bold',
                                        fontFamily: theme.typography.fontFamily
                                    }}
                                >
                                    {title}
                                </Typography>
                                <Box sx={{width:'40px', height:'3px', backgroundColor:'black'}}></Box>
                                <Typography component={'p'} sx={{fontFamily: theme.typography.fontFamily}}>{subtitle}</Typography>
                            </Box>
                            <Box sx={{alignSelf: 'center'}}>
                                {!hideSearch && <TextField
                                    placeholder={'Search...'}
                                    size="small"
                                    inputRef={inputRef}
                                    InputProps={{
                                        sx: {
                                            borderRadius: '40px',
                                            // borderColor: theme.palette.secondary.main,
                                            color: theme.palette.secondary.dark,
                                            backgroundColor: theme.palette.secondary.main,
                                        },
                                        onKeyDown: (event) => {
                                            if(event.key === 'Enter') {
                                                performSearch();
                                            }
                                        }
                                    }}
                                    InputLabelProps={{
                                        sx: {
                                            fontFamily: theme.typography.fontFamily,
                                            color: theme.palette.secondary.dark
                                        }
                                    }}

                                    sx={{
                                        width: isSearchOpen ? '160px' : '40px',
                                        transition: 'width 0.4s ease-in-out',
                                        opacity: isSearchOpen ? 1 : 0,
                                        position: 'relative',
                                        left: '39px',
                                        '& .MuiOutlinedInput-root': {
                                            '& fieldset': {
                                                'borderColor': theme.palette.secondary.main
                                            },
                                            '& .MuiInputBase-input': {
                                                zIndex: 1
                                            },
                                        }
                                    }}
                                    value={searchedText ?? ''}
                                    onChange={(event) => $.set('Searched Text', event.target.value)}
                                    onBlur={handleSearchEnd}
                                />}
                                {!hideSearch && <Icon baseClassName={'material-icons-outlined'}
                                      sx={{
                                        ...iconStyle,
                                        backgroundColor: isSearchOpen ? 'transparent' : theme.palette.secondary.main
                                    }}
                                      onClick={openSearch}>
                                    search
                                </Icon>}


                                {!hideNotifications && <Box display={'inline-block'} position={'relative'}>
                                    <Icon baseClassName={'material-icons-outlined'} sx={iconStyle}
                                          onClick={() => $.trigger('On Notifications')}>
                                        notifications
                                    </Icon>
                                    <Box sx={{display: notifCount ? 'inline-block': 'none', position: 'absolute', backgroundColor: theme.palette.primary.main, color: 'white', zIndex: 3, cursor: 'pointer',
                                        borderRadius: '10px', left: '25px', top: '25px', width: '20px', height: '20px', textAlign: 'center', visibility: notifCount ? 'visible' : 'hidden'}}
                                         onClick={() => $.trigger('On Notifications')}
                                    >
                                        <Typography component={'p'} sx={{fontFamily: theme.typography.fontFamily, lineHeight: 1.25}}>{notifCount}</Typography>
                                    </Box>
                                </Box>}

                                {!hiderUserIcon && <Box display={'inline-block'} position={'relative'}
                                     sx={{
                                         backgroundColor: theme.palette.secondary.main,
                                         borderRadius: 40,
                                         height: '40px',
                                         width: userMenuList?.length > 0 ? '70px' : '40px',
                                         cursor: 'pointer',
                                         marginBottom: '5px',
                                    }}
                                     onClick={(event) => {setAnchorEl(event.currentTarget); setMenuOpen(!isMenuOpen)}}>
                                    <Box display={'inline-block'} position={'absolute'}
                                         sx={{backgroundColor: theme.palette.primary.main, borderRadius: '15px', width: '30px', height: '30px', top: '5px', left: '5px', textAlign: 'center', zIndex:3}}>
                                        <Typography component={'p'} sx={{fontFamily: theme.typography.fontFamily, fontSize: '18px', lineHeight: '30px', color: 'white'}}>
                                            {userSlang}
                                        </Typography>
                                    </Box>
                                    {userMenuList?.length > 0 && <>
                                        <Icon baseClassName={'material-icons-outlined'} sx={{...iconStyle, marginLeft: '30px', boxShadow: 'none', backgroundColor: 'transparent'}}>
                                            {isMenuOpen ? 'keyboard_arrow_up' : 'keyboard_arrow_down'}
                                        </Icon>
                                        <Menu disableAutoFocusItem
                                            anchorEl={anchorEl}
                                            open={isMenuOpen}
                                            onClose={() => setMenuOpen(false)}
                                            sx={{
                                                "& .MuiPaper-root": {
                                                    backgroundColor: theme.palette.secondary.main,
                                                    borderRadius: '10px'
                                                }
                                            }}
                                        >
                                            {userMenuList.split(',').map((item: string, index: number) => {
                                                const menuName = item.trim();
                                                return <MenuItem key={index} onClick={() => handleUserMenuClick(menuName)} sx={{color: theme.palette.secondary.dark}} dense>{menuName}</MenuItem>
                                            })}
                                        </Menu>
                                    </>}
                                </Box>}
                            </Box>
                        </Box>
                    </ThemeProvider>
                )
            );
        };
    }
}

registerBrick('019175869496df2d621b', Header);
